import { apiErrorMessage } from '@/services/apiErrorMessage.js'

/**
 * Bulk-reject a set of pending plans by fanning out over the existing
 * per-plan decision endpoint (`POST /plans/{planId}/decide`).
 *
 * There is no server-side bulk endpoint. Each plan is an independent
 * decision, so we issue one request per plan and aggregate the outcomes.
 * `request` is injected (from `useOrchestratorRequest`) for testability and
 * returns `{ ok, status, data }` without throwing on HTTP errors — network
 * rejections are captured by `Promise.allSettled`.
 *
 * No outcome is discarded: EVERY input id lands in exactly one of `succeeded`
 * or `failed`. An unusable id (non-string or empty) is never silently dropped
 * — it is surfaced as a `failed` entry with an "Invalid plan id." reason so
 * the caller's count/summary reflects every selected plan instead of showing
 * a misleadingly low total. `failed` carries the surfaced error message per
 * plan so the caller can report partial failures rather than swallowing them.
 *
 * @param {(path: string, options?: object) => Promise<{ok: boolean, status: number, data: unknown}>} request
 * @param {string[]} planIds
 * @param {{ reason?: string }} [options]
 * @returns {Promise<{ succeeded: string[], failed: Array<{ planId: string, error: string }> }>}
 */
export async function bulkRejectPlans(request, planIds, { reason } = {}) {
  const ids = Array.isArray(planIds) ? planIds : []
  const trimmedReason = typeof reason === 'string' ? reason.trim() : ''

  const outcomes = await Promise.allSettled(ids.map(async (planId) => {
    if (typeof planId !== 'string' || !planId) {
      // Do NOT drop the id — surface it as a failure so it is counted. A
      // malformed/empty id must never vanish from both succeeded and failed.
      throw new Error('Invalid plan id.')
    }

    const response = await request(`/plans/${planId}/decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        decision: 'reject',
        ...(trimmedReason ? { reason: trimmedReason } : {}),
      }),
    })

    if (!response?.ok) {
      throw new Error(apiErrorMessage(response?.data, response?.status))
    }
    return planId
  }))

  const succeeded = []
  const failed = []

  outcomes.forEach((outcome, index) => {
    const rawId = ids[index]
    if (outcome.status === 'fulfilled') {
      succeeded.push(outcome.value)
    } else {
      // Stringify so a non-string id still has a displayable identity in the
      // failure list (null/undefined/empty collapse to '').
      const planId = typeof rawId === 'string' ? rawId : String(rawId ?? '')
      const reasonMessage = outcome.reason instanceof Error
        ? outcome.reason.message
        : String(outcome.reason)
      failed.push({ planId, error: reasonMessage || 'Failed to reject plan.' })
    }
  })

  return { succeeded, failed }
}
